import { useState, useEffect } from 'react';
import { getCityCoordinates } from '../utils/distance';

interface UserLocation {
  lat: number;
  lon: number;
  source: 'geolocation' | 'city' | 'default';
}

interface UseUserLocationOptions {
  requestGeolocation?: boolean;
}

export const useUserLocation = (selectedCity?: string, options: UseUserLocationOptions = {}) => {
  const { requestGeolocation = false } = options;
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const getLocation = async () => {
      // Only try geolocation if explicitly requested
      if (requestGeolocation && 'geolocation' in navigator) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 5000,
              maximumAge: 300000, // Cache for 5 minutes
            });
          });

          setLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            source: 'geolocation',
          });
          setLoading(false);
          return;
        } catch (err) {
          // Geolocation failed, fall back to city coordinates
          console.log('Geolocation not available, using city coordinates');
        }
      }

      // Use selected city or default to Houston
      const cityToUse = selectedCity || 'Houston';
      const coords = getCityCoordinates(cityToUse);

      if (coords) {
        setLocation({
          ...coords,
          source: 'city',
        });
      } else {
        setError('Unable to determine location');
      }

      setLoading(false);
    };

    getLocation();
  }, [selectedCity, requestGeolocation]);

  return { location, loading, error };
};
